/**
 * Single source of truth for experience duration banding. The storefront
 * filter panel, the destination filter, the card badge, and the API's
 * duration-range SQL all derive from these definitions so the vocabulary
 * never drifts across the api/web boundary.
 *
 * A package's duration is first reduced to total minutes (matching the
 * filter SQL), then mapped to a band. A package of 4 hours or less is a
 * "half day".
 */

/** The canonical set of package duration units — the single source every
 *  schema, type, and SQL CASE derives from. Ordered shortest → longest.
 *  `as const` so it doubles as a Zod enum tuple and a literal-union source. */
export const DURATION_UNITS = [
  "hours",
  "hours_minutes",
  "days",
  "days_nights",
  "weeks",
] as const;

export type DurationUnit = (typeof DURATION_UNITS)[number];

export type DurationBandId = "half" | "full" | "short" | "week" | "long";

/** Minutes in one primary unit. `hours_minutes` adds its secondary (minutes);
 *  `days_nights` ignores its secondary (nights) for length. */
export const DURATION_UNIT_MINUTES: Record<DurationUnit, number> = {
  hours: 60,
  hours_minutes: 60,
  days: 1440,
  days_nights: 1440,
  weeks: 10080,
};

/** Days in one primary unit, for units measured in days. Hour-based units are
 *  absent on purpose: an hours-long activity has no day count. */
export const DAYS_PER_DURATION_UNIT: Partial<Record<DurationUnit, number>> = {
  days: 1,
  days_nights: 1,
  weeks: 7,
};

/** Units whose `secondary` component adds to the total length. Only
 *  `hours_minutes` does (its secondary is minutes); `days_nights` ignores its
 *  secondary (nights) for length. Single source so the TS reducer and the
 *  API's SQL CASE branch on the same set instead of each hard-coding the
 *  `=== "hours_minutes"` test. */
export const DURATION_UNITS_WITH_SECONDARY: ReadonlySet<DurationUnit> = new Set(
  ["hours_minutes"],
);

/** True when a `days_nights` duration claims more nights than days — "1 Day,
 *  3 Nights", which no trip can be.
 *
 *  Only a strictly greater night count is rejected. N days / N-1 nights is the
 *  usual reading and N/N is legitimate when the traveller arrives the night
 *  before, so both stay valid.
 *
 *  Every other unit is exempt, and that is the whole reason this lives beside
 *  `DURATION_UNITS_WITH_SECONDARY` rather than being written inline twice:
 *  `secondary` is a night count for exactly one unit. For `hours_minutes` it is
 *  MINUTES, where 30 against 2 hours is ordinary — a blanket "secondary must
 *  not exceed value" rule would reject "2 hours 30 minutes". */
export function hasMoreNightsThanDays(
  unit: DurationUnit | undefined,
  days: number | null | undefined,
  nights: number | null | undefined,
): boolean {
  if (unit !== "days_nights") return false;
  if (typeof days !== "number" || typeof nights !== "number") return false;
  return nights > days;
}

export function durationToMinutes(
  unit: DurationUnit,
  value: number,
  secondary = 0,
): number {
  const base = DURATION_UNIT_MINUTES[unit] * value;
  return DURATION_UNITS_WITH_SECONDARY.has(unit) ? base + secondary : base;
}

export interface DurationBand {
  id: DurationBandId;
  /** Filter-panel label (experiences page). */
  label: string;
  /** Compact card-badge label. */
  cardLabel: string;
  minMinutes?: number;
  maxMinutes?: number;
}

/** ≤ 4 hrs (240 min) is a half day; 1 day = 1440 min. */
export const DURATION_BANDS: readonly DurationBand[] = [
  {
    id: "half",
    label: "Half day (≤ 4 hrs)",
    cardLabel: "Half day",
    maxMinutes: 240,
  },
  {
    id: "full",
    label: "1 day",
    cardLabel: "1 day",
    minMinutes: 241,
    maxMinutes: 1440,
  },
  {
    id: "short",
    label: "2 – 3 days",
    cardLabel: "2–3 days",
    minMinutes: 1441,
    maxMinutes: 4320,
  },
  {
    id: "week",
    label: "4 – 7 days",
    cardLabel: "4–7 days",
    minMinutes: 4321,
    maxMinutes: 10080,
  },
  { id: "long", label: "8+ days", cardLabel: "8+ days", minMinutes: 10081 },
];

export function bandForMinutes(minutes: number): DurationBand | null {
  return (
    DURATION_BANDS.find(
      (b) =>
        (b.minMinutes === undefined || minutes >= b.minMinutes) &&
        (b.maxMinutes === undefined || minutes <= b.maxMinutes),
    ) ?? null
  );
}

export interface PackageDurationLike {
  /** Accepts the raw DB/DTO string; validated against the known units below. */
  durationUnit?: string | null;
  durationValue?: number | null;
  durationSecondary?: number | null;
}

/** A package's total length in minutes, or null when it has no valid, positive
 *  duration set. The single reducer that `bandForPackage`, the storefront card
 *  label, and the duration-sort SQL's TS side all share. */
export function packageDurationMinutes(
  pkg: PackageDurationLike,
): number | null {
  const unit = pkg.durationUnit;
  // `includes` over a known tuple, not the `in` operator: `in` walks the
  // prototype chain, so "constructor"/"toString" would pass the guard and
  // then index a function out of DURATION_UNIT_MINUTES.
  if (
    !unit ||
    !(DURATION_UNITS as readonly string[]).includes(unit) ||
    pkg.durationValue == null
  ) {
    return null;
  }
  const minutes = durationToMinutes(
    unit as DurationUnit,
    pkg.durationValue,
    pkg.durationSecondary ?? 0,
  );
  // A total of 0 minutes is an unset/placeholder duration, not a half day.
  // Guard on the total (not durationValue) so a genuine "0h 30m" — value 0,
  // secondary 30 under hours_minutes — still counts.
  return minutes > 0 ? minutes : null;
}

/** A package's length in days, or null when it isn't measured in days (an
 *  hours-long activity has no day count) or has no positive length. Same
 *  unit guard as `packageDurationMinutes`. */
export function packageDurationDays(pkg: PackageDurationLike): number | null {
  const unit = pkg.durationUnit;
  if (!unit || !(DURATION_UNITS as readonly string[]).includes(unit)) {
    return null;
  }
  const daysPerUnit = DAYS_PER_DURATION_UNIT[unit as DurationUnit];
  const value = pkg.durationValue ?? 0;
  return daysPerUnit && value > 0 ? value * daysPerUnit : null;
}

export function bandForPackage(pkg: PackageDurationLike): DurationBand | null {
  const minutes = packageDurationMinutes(pkg);
  return minutes != null ? bandForMinutes(minutes) : null;
}
